import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Baby, Heart, Cake, Sparkles, Feather } from "lucide-react";

// Compute duration between now and target date/time
function useCountdown(dateStr, timeStr) {
  const target = useMemo(() => {
    if (!dateStr) return null;
    const t = timeStr && /^\d{2}:\d{2}/.test(timeStr) ? timeStr : "12:00";
    const d = new Date(`${dateStr}T${t}:00`);
    return isNaN(d.getTime()) ? null : d;
  }, [dateStr, timeStr]);
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) return { days: 0, hours: 0, minutes: 0, seconds: 0, done: false, invalid: true };
  const diff = Math.max(0, target - now);
  const done = diff === 0;
  const s = Math.floor(diff / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  return { days, hours, minutes, seconds, done, invalid: false };
}

const themeConfig = {
  baby_reveal: { icon: Baby, floatEmoji: null, floaters: ["●", "★", "◆"], colorSoft: "#D9AEB0", label: "Nuestros corazones ya lo saben, pero lo sabremos juntos en..." },
  boda: { icon: Heart, floatEmoji: null, floaters: ["✿", "♡", "✧"], colorSoft: "#C5A880", label: "Faltan para el sí, acepto" },
  xv_anos: { icon: Sparkles, floatEmoji: null, floaters: ["✦", "✧", "★"], colorSoft: "#D28E75", label: "Faltan para mis XV" },
  cumpleanos: { icon: Cake, floatEmoji: null, floaters: ["●", "◆", "★"], colorSoft: "#9DB0A3", label: "Faltan para el cumple" },
  bautizo: { icon: Feather, floatEmoji: null, floaters: ["✧", "✿", "☁"], colorSoft: "#7A826B", label: "Faltan para el bautizo" },
  otros: { icon: Sparkles, floatEmoji: null, floaters: ["✦", "●", "✧"], colorSoft: "#B08968", label: "Faltan para el evento" },
};

function Digit({ value, small = false }) {
  const v = String(value).padStart(2, "0");
  return (
    <div
      className={`relative inline-block shrink-0 px-1.5 min-w-[2.2ch] ${
        small ? "h-[2.2rem] sm:h-[2.6rem] md:h-[3rem]" : "h-[3.4rem] sm:h-[4.2rem] md:h-[5rem]"
      }`}
      style={{ clipPath: "inset(0px -1.5rem)" }}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={v}
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className={`block headline text-ink tabular-nums ${
            small
              ? "text-3xl sm:text-4xl md:text-[2.6rem] leading-[2.2rem] sm:leading-[2.6rem] md:leading-[3rem]"
              : "text-5xl sm:text-6xl md:text-7xl leading-[3.4rem] sm:leading-[4.2rem] md:leading-[5rem]"
          }`}
        >
          {v}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}

export default function Countdown({ theme = "baby_reveal", date, time, label }) {
  const cfg = themeConfig[theme] || themeConfig.baby_reveal;
  const { days, hours, minutes, seconds, done } = useCountdown(date, time);
  const Icon = cfg.icon;

  return (
    <div className="relative py-14 md:py-20 overflow-hidden">
      {/* floating decorative elements */}
      {cfg.floaters.map((f, i) => (
        <span
          key={i}
          aria-hidden
          className="absolute select-none pointer-events-none animate-float-slow"
          style={{
            top: `${10 + i * 22}%`,
            left: `${(i * 27) % 90}%`,
            color: cfg.colorSoft,
            fontSize: `${36 + (i % 3) * 12}px`,
            animationDelay: `${i * 0.7}s`,
            opacity: 0.55,
          }}
        >
          {f}
        </span>
      ))}
      {[0, 1, 2].map((i) => (
        <span
          key={`b-${i}`}
          aria-hidden
          className="absolute select-none pointer-events-none animate-float-med"
          style={{
            bottom: `${8 + i * 18}%`,
            right: `${(i * 33) % 80}%`,
            color: cfg.colorSoft,
            fontSize: `${14 + (i % 2) * 5}px`,
            animationDelay: `${i * 0.9}s`,
            opacity: 0.45,
          }}
        >
          {cfg.floaters[i % cfg.floaters.length]}
        </span>
      ))}

      <div className="relative text-center fn-editorial">
        <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-4">
          <Icon size={14} className="text-warmgold" />
          <span className="text-xs tracking-widest uppercase text-ink/80">{label || cfg.label}</span>
        </div>

        {done ? (
          <p className="script text-5xl md:text-6xl text-warmgold mt-4">¡Hoy es el día!</p>
        ) : (
          <div className="mt-4 flex flex-nowrap items-end justify-center gap-1.5 sm:gap-2 md:gap-5">
            {[
              { v: days, l: "días" },
              { v: hours, l: "hrs" },
              { v: minutes, l: "min" },
              { v: seconds, l: "seg", small: true },
            ].map((u, idx) => (
              <div key={u.l} className="flex flex-col items-center">
                <div
                  className={`flex shrink-0 glass rounded-2xl justify-center ${
                    u.small
                      ? "px-2 py-1.5 md:px-3 md:py-2 min-w-[46px] md:min-w-[68px]"
                      : "px-3 py-2 md:px-5 md:py-3 min-w-[58px] md:min-w-[100px]"
                  }`}
                >
                  <Digit value={u.v} small={u.small} />
                </div>
                <span className={`tracking-[0.2em] uppercase text-inksoft mt-2 ${u.small ? "text-[9px] md:text-[10px]" : "text-[10px] md:text-xs"}`}>{u.l}</span>
                {idx < 3 && <span className="sr-only">:</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
